/**
 * Форматирует число с указанным количеством знаков после запятой.
 * Убирает лишние нули в конце и точку, если десятичной части нет.
 *
 * @param {number|string} num - Число, которое нужно отформатировать.
 * @param {number} [digits=2] - Количество знаков после запятой.
 * @returns {string} Отформатированное число.
 *
 * @example
 * formatNumber(12.345)        // "12.35"
 * formatNumber(12.300)        // "12.3"
 * formatNumber(12)            // "12"
 * formatNumber(null)          // "0"
 */
export function formatNumber(num, digits = 2) {
  return num ? (+num).toFixed(digits).replace(/(\.0*|(?:\/(\..*))0*)$/, '') : '0'
}
/**
 * Форматирует число как процент.
 * Использует formatNumber и добавляет знак '%'.
 *
 * @param {number|string} num - Исходное число.
 * @param {number} [digits=2] - Количество знаков после запятой.
 * @returns {string} Отформатированный процент.
 *
 * @example
 * formatAsPercentage(0.1234)   // "0.12%"
 * formatAsPercentage(null)     // "0%"
 */
export function formatAsPercentage(num, digits = 2) {
  return num ? formatNumber(num, digits) + '%' : '0%'
}
/**
 * Округляет число до двух знаков, но оставляет ноль как есть.
 * Если значение пустое (кроме 0) — возвращает пустую строку.
 *
 * @param {number|string} value - Значение для округления.
 * @param {Object} [opts]
 * @param {boolean} [opts.preciseFraction=false] - Для значений от 0 до 1 (не включая 0) сохранять 5 знаков после запятой вместо 2.
 * @param {boolean} [opts.trimTrailingZeros=false] - Актуально только вместе с preciseFraction: убрать хвостовые нули (например, 0.10000 -> "0.1").
 * @returns {string|number} Отформатированное значение.
 *
 * @example
 * formatTrimmedNumber(12.345)   // "12.35"
 * formatTrimmedNumber(0)        // 0
 * formatTrimmedNumber(null)     // ""
 * formatTrimmedNumber(0.03456, { preciseFraction: true })   // "0.03456"
 */
export function formatTrimmedNumber(value, { preciseFraction = false, trimTrailingZeros = false } = {}) {
  if (!value && value !== 0) return ''
  const num = Number(value)
  if (num === 0) return value
  if (preciseFraction && num > 0 && num < 1) {
    return trimTrailingZeros ? Number(num.toFixed(5)).toString() : num.toFixed(5)
  }
  return num.toFixed(2)
}

/**
 * То же самое, что formatTrimmedNumber, но для значений от 0 до 1 (не включая 0) сохраняет 5 знаков после запятой
 * вместо 2. Используется как в UI-таблицах (GeneralReportMixin, ListPartnerReport, TablePaymentInvoice), так и
 * при экспорте (report-export.js).
 *
 * @param {number|string} value - Значение для округления.
 * @param {Object} [opts]
 * @param {boolean} [opts.trimTrailingZeros=false] - Убрать хвостовые нули у значений от 0 до 1 (например, 0.10000 -> "0.1").
 * @returns {string|number} Отформатированное значение.
 *
 * @example
 * formatTrimmedNumberPrecise(12.345)    // "12.35"
 * formatTrimmedNumberPrecise(0.03456)   // "0.03456"
 * formatTrimmedNumberPrecise(0.1, { trimTrailingZeros: true })   // "0.1"
 * formatTrimmedNumberPrecise(0)         // 0
 * formatTrimmedNumberPrecise(null)      // ""
 */
export function formatTrimmedNumberPrecise(value, { trimTrailingZeros = false } = {}) {
  return formatTrimmedNumber(value, { preciseFraction: true, trimTrailingZeros })
}

/**
 * Округляет число до указанного количества знаков после запятой по правилам математики (round half up).
 *
 * @param {number|string} value - Значение для округления.
 * @param {number} [digits=1] - Количество знаков после запятой.
 * @returns {string} Отформатированное значение.
 *
 * @example
 * formatRoundHalfUp(1.54)   // "1.5"
 * formatRoundHalfUp(1.56)   // "1.6"
 * formatRoundHalfUp(1.55)   // "1.6"
 */
export function formatRoundHalfUp(value, digits = 0) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (!Number.isFinite(num)) return ''

  const factor = Math.pow(10, digits)
  const epsilon = Number.EPSILON * Math.max(1, Math.abs(num))

  return (Math.round((num + epsilon) * factor) / factor).toFixed(digits)
}


/**
 * Округляет число до двух знаков, если есть дробное значение. Если
 * целое число - оставляет, как есть. Если 0 или отсутствует значение - "-".
 * Используется как замена компонента ui-saving-input
 * @param value - значение для форматирования
 * @param showZero - показывать 0 вместо прочерка
 * @returns {string} - отформатированное число
 *
 * @example
 * numberInputPlug(12.345)   // "12.35"
 * numberInputPlug(12.3)     // "12.30"
 * numberInputPlug("42")     // "42"
 * numberInputPlug("abc")    // "42"
 * numberInputPlug(0)        // "-" или "0"
 * numberInputPlug(null)     // "-"
 */
export function numberInputPlug(value, showZero = false) {
  const num = Number(value)
  if (!value && value !== 0) return '-'
  if (num === 0 && !showZero) return '-'
  if (isNaN(num)) return '-'
  if (Number.isInteger(num)) return String(num)
  return num.toFixed(2)
}